/**
 * MonitorSignupPage — `/monitor/start`. Public, no auth, no shell.
 *
 * The front door a real external client walks through: name a workspace,
 * get started. Leads with the pain/benefit ("see where your business is
 * leaking money — and prove the fix"), never the technology. On submit it
 * calls the unauthenticated signup endpoint, saves the returned token in
 * localStorage, shows it once with a "save this" note, then routes to the
 * connect screen.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import { signup, saveMonitorSession, MonitorError } from './monitorClient';

const ink = '#111827';
const inkMuted = '#4b5563';
const inkSubtle = '#6b7280';
const rim = '#e5e7eb';
const accent = '#1d4ed8';

export function MonitorSignupPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string>('');
  const [token, setToken] = useState<string>('');
  const [copied, setCopied] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') return;
    setStatus('submitting');
    setError('');
    try {
      const res = await signup(trimmed);
      saveMonitorSession({
        token: res.api_token,
        ingestUrl: res.ingest_url,
        workspace: trimmed,
      });
      setToken(res.api_token);
      setStatus('done');
    } catch (err) {
      setError(
        err instanceof MonitorError
          ? err.message
          : 'Something went wrong creating your workspace. Please try again.',
      );
      setStatus('error');
    }
  }

  async function copyToken() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard may be blocked; the token is visible to copy by hand */
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#ffffff',
        color: ink,
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '18px 24px',
          borderBottom: `1px solid ${rim}`,
        }}
      >
        <GiOrbitLogo size="sm" />
        <span style={{ fontSize: 14, fontWeight: 600 }}>Growing Intelligence</span>
      </header>

      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
        }}
      >
        <div style={{ width: '100%', maxWidth: 560 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: accent,
            }}
          >
            Monitor
          </span>
          <h1 style={{ fontSize: 32, fontWeight: 800, margin: '12px 0 14px', lineHeight: 1.12 }}>
            See where your business is leaking money — and prove the fix.
          </h1>
          <p style={{ fontSize: 16, color: inkMuted, margin: '0 0 28px', lineHeight: 1.55 }}>
            Monitor learns your business, finds the waste, and shows you exactly what to fix —
            in plain language. Every result is signed, so you can prove the savings to anyone.
          </p>

          {status !== 'done' ? (
            <form onSubmit={handleSubmit}>
              <label
                htmlFor="workspace-name"
                style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}
              >
                Name your workspace
              </label>
              <input
                id="workspace-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Acme Coffee Roasters"
                autoFocus
                style={{
                  width: '100%',
                  fontSize: 15,
                  padding: '12px 14px',
                  border: `1px solid ${rim}`,
                  borderRadius: 8,
                  outline: 'none',
                  boxSizing: 'border-box',
                  marginBottom: 16,
                }}
              />
              {status === 'error' && (
                <p style={{ fontSize: 13, color: '#b91c1c', margin: '0 0 14px' }}>{error}</p>
              )}
              <button
                type="submit"
                disabled={status === 'submitting' || name.trim() === ''}
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: '#ffffff',
                  background: name.trim() === '' ? '#9ca3af' : accent,
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 22px',
                  cursor:
                    status === 'submitting' || name.trim() === '' ? 'default' : 'pointer',
                }}
              >
                {status === 'submitting' ? 'Setting up…' : 'Get started — free'}
              </button>
              <p style={{ fontSize: 12, color: inkSubtle, margin: '16px 0 0', lineHeight: 1.5 }}>
                Your data stays yours. No credit card. You can connect a source in the next step.
              </p>
            </form>
          ) : (
            <div>
              <div
                style={{
                  border: `1px solid ${rim}`,
                  borderRadius: 10,
                  padding: '18px 20px',
                  background: '#f9fafb',
                  marginBottom: 22,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                  Save this access key
                </div>
                <p style={{ fontSize: 13, color: inkMuted, margin: '0 0 12px', lineHeight: 1.5 }}>
                  This is your private key for connecting your systems. We show it once. Keep it
                  somewhere safe — anyone with this key can send data to your workspace.
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: '#ffffff',
                    border: `1px solid ${rim}`,
                    borderRadius: 8,
                    padding: '10px 12px',
                  }}
                >
                  <code
                    style={{
                      flex: 1,
                      fontSize: 13,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {token}
                  </code>
                  <button
                    type="button"
                    onClick={copyToken}
                    style={{
                      flexShrink: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      color: accent,
                      background: '#ffffff',
                      border: `1px solid ${rim}`,
                      borderRadius: 6,
                      padding: '6px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/monitor/connect')}
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: '#ffffff',
                  background: accent,
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 22px',
                  cursor: 'pointer',
                }}
              >
                Connect your business →
              </button>
            </div>
          )}

          <p style={{ fontSize: 12, color: inkSubtle, margin: '40px 0 0', lineHeight: 1.5 }}>
            Your data stays yours. Every result is signed — verify it yourself, anytime.
          </p>
        </div>
      </main>
    </div>
  );
}

export default MonitorSignupPage;
